import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { Container, Group, Stack, Badge, Space } from '@mantine/core';
import dayjs from 'dayjs';

import Card from 'components/Card';
import PageTitle from 'components/PageTitle';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';

import { rangeTemplateService, RangeTemplate } from 'services/lia/rangeTemplateService';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const RangeTemplatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const searchParams = new URLSearchParams(location.search);
  const defaultSearch = searchParams.get('search') || '';

  // Keep track of previous search to detect changes
  const [previousSearch, setPreviousSearch] = useState(defaultSearch);

  const [selectedTemplate, setSelectedTemplate] = useState<RangeTemplate | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (defaultSearch !== previousSearch) {
      setPage(1);
      setPreviousSearch(defaultSearch);
    }
  }, [defaultSearch, previousSearch]);

  const [columns, setColumns] = useState([
    {
      heading: 'Template Name',
      props: (row: RangeTemplate) =>
        row.templateName.length > 50 ? row.templateName.substring(0, 50) + '...' : row.templateName,
      sortable: true,
      active: true,
      id: 'templateName'
    },
    {
      heading: 'Description',
      props: 'description',
      sortable: true,
      active: true,
      id: 'description'
    },
    {
      heading: 'Items',
      props: (row: RangeTemplate) => (
        <Group gap='xs'>
          {row.items?.slice(0, 3).map((item, idx) => (
            <Badge key={idx} color='gray' variant='outline' size='sm'>
              {item.label}
            </Badge>
          ))}
          {row.items?.length > 3 && (
            <Badge color='gray' variant='outline' size='sm'>
              +{row.items.length - 3}
            </Badge>
          )}
        </Group>
      ),
      active: true,
      id: 'items'
    },
    {
      heading: 'Created By',
      props: 'userIn',
      sortable: true,
      active: true,
      id: 'userIn'
    },
    {
      heading: 'Created Date',
      props: (row: RangeTemplate) => dayjs(row.dateIn).format('DD MMM YYYY HH:mm'),
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Updated By',
      props: (row: RangeTemplate) => row.userUp || '-',
      sortable: true,
      active: true,
      id: 'userUp'
    },
    {
      heading: 'Updated Date',
      props: (row: RangeTemplate) =>
        row.dateUp ? dayjs(row.dateUp).format('DD MMM YYYY HH:mm') : '-',
      sortable: true,
      active: true,
      id: 'dateUp'
    },
    {
      heading: 'Action',
      props: 'action',
      active: true,
      id: 'action'
    }
  ]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['rangeTemplates', defaultSearch, page, pageSize],
    queryFn: () =>
      rangeTemplateService.getList({
        search: defaultSearch,
        pageNumber: page,
        pageSize: pageSize
      }),
    staleTime: 30000
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['rangeTemplates'] });
  }, [queryClient]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => rangeTemplateService.delete(id),
    onSuccess: () => {
      refetch();
      showSuccessNotification({
        title: 'Deleted Successfully!',
        message: 'Range template has been deleted successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to delete template',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  // Handle view template
  const handleView = (template: RangeTemplate) => {
    // Navigate in the same tab
    const url = `/app/lia/questions/range-templates/view?id=${template.msRangeTemplateID}`;
    navigate(url);
  };

  // Handle edit template
  const handleEdit = (template: RangeTemplate) => {
    // Navigate in the same tab
    const url = `/app/lia/questions/range-templates/edit?id=${template.msRangeTemplateID}`;
    navigate(url);
  };

  // Handle delete confirmation
  const handleDelete = (template: RangeTemplate) => {
    setSelectedTemplate(template);
    open();
  };

  // Handle delete confirmation submit
  const handleConfirmDelete = () => {
    if (selectedTemplate?.msRangeTemplateID) {
      deleteMutation.mutate(selectedTemplate.msRangeTemplateID);
    }
    close();
  };

  const handleCreateClick = () => {
    // Navigate in the same tab
    navigate('/app/lia/questions/range-templates/add');
  };

  // Prepare data for DataTables component with proper pagination info
  const tableData = {
    data: data?.data || [],
    pageNumber: data?.pageNumber || 1,
    pageSize: data?.pageSize || 10,
    totalPages: data?.totalPages || 0,
    totalRecords: data?.totalRecords || 0
  };

  return (
    <Container fluid>
      <Stack gap='md'>
        <PageTitle
          title='Range Templates'
          description='Manage predefined range option templates for your questions'
        />

        <Card>
          <Toolbar
            showSearch
            searchPlaceholder='Search templates...'
            showFilter={false}
            showCreateButton
            createButtonText='Add New Template'
            defaultSearchValue={defaultSearch}
            onCreate={handleCreateClick}
            showFilterColumn={true}
            setColumns={setColumns}
          />
          <Space h={16} />

          <DataTables
            columns={columns}
            data={tableData.data}
            loading={isLoading || deleteMutation.isPending}
            activeSize={pageSize}
            sizing={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 }
            ]}
            withPagination
            totalData={tableData.totalRecords}
            currentPage={page}
            currentSize={pageSize}
            handleChangePage={handlePageChange}
            handleChangeSize={handleSizeChange}
            withBulkData={false}
            viewText='View'
            editText='Edit'
            deleteText='Delete'
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            emptyMessageTitle='No templates found'
            emptyMessageDescription='Create a new range template to get started'
          />
        </Card>
      </Stack>

      <ModalPopupConfirmationComponent
        buttonText='Delete'
        statusProcess='delete'
        opened={opened}
        open={open}
        close={close}
        onSubmit={handleConfirmDelete}
      />
    </Container>
  );
};

export default RangeTemplatePage;
